import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowDownLeft, ExternalLink, Clock } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BuyerTransfersPage = () => {
  const navigate = useNavigate();
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTransfers();
  }, []);

  const fetchTransfers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/buyer/transfers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransfers(Array.isArray(res.data.transfers) ? res.data.transfers : []);
    } catch (error) {
      console.error('Error fetching transfers:', error);
      toast.error('Failed to load transfers');
      setTransfers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTransferClick = (transferId) => {
    navigate(`/buyer/transfers/${transferId}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeBadge = (type) => {
    const styles = {
      'PURCHASE': { bg: 'rgba(71, 255, 176, 0.1)', color: '#47FFB0', label: 'Purchase' },
      'TRANSFER_IN': { bg: 'rgba(75, 157, 255, 0.1)', color: '#4B9DFF', label: 'Received' },
      'TRANSFER_OUT': { bg: 'rgba(255, 107, 107, 0.1)', color: '#FF6B6B', label: 'Sent' }
    };
    const style = styles[type] || styles.PURCHASE;
    return (
      <span style={{
        fontSize: '11px',
        padding: '4px 8px',
        borderRadius: '4px',
        fontWeight: 600,
        background: style.bg,
        color: style.color
      }}>
        {style.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const isCompleted = status === 'COMPLETED';
    return (
      <span style={{
        fontSize: '11px',
        padding: '4px 8px',
        borderRadius: '4px',
        fontWeight: 600,
        background: isCompleted ? 'rgba(71, 255, 176, 0.1)' : 'rgba(255, 193, 7, 0.1)',
        color: isCompleted ? '#47FFB0' : '#FFC107'
      }}>
        {status}
      </span>
    );
  };

  return (
    <div style={{ padding: '40px' }}>
      <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#0B0B0B', marginBottom: '8px' }}>
        Transfers
      </h1>
      <p style={{ fontSize: '16px', color: '#999', marginBottom: '32px' }}>
        View your NFT transfer history
      </p>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#999' }}>
          Loading transfers...
        </div>
      ) : transfers.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {transfers.map(t => (
            <div
              key={t.id}
              onClick={() => handleTransferClick(t.id)}
              data-testid={`transfer-item-${t.id}`}
              style={{
                background: '#FFFFFF',
                borderRadius: '12px',
                padding: '20px',
                border: '1px solid #E6E8EF',
                cursor: 'pointer',
                transition: 'border-color 0.2s, transform 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#4B9DFF';
                e.currentTarget.style.transform = 'translateX(4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#333';
                e.currentTarget.style.transform = 'translateX(0)';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                {/* Icon */}
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: t.image_url 
                    ? `url(${t.image_url}) center/cover`
                    : 'linear-gradient(135deg, #7C3AED, #4B9DFF)',
                  flexShrink: 0
                }} />

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ fontSize: '16px', fontWeight: 600, color: '#0B0B0B' }}>
                      {t.portfolio_name}
                    </span>
                    {getTypeBadge(t.type)}
                  </div>
                  <div style={{ fontSize: '13px', color: '#999' }}>
                    {t.serial ? `Token ${t.serial}` : `Token #${t.token_id || 'N/A'}`}
                  </div>
                </div>

                {/* Right side */}
                <div style={{ textAlign: 'right' }}>
                  <div style={{ marginBottom: '4px' }}>
                    {getStatusBadge(t.status)}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#666' }}>
                    <Clock size={12} />
                    {formatDate(t.occurred_at)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{
          background: '#FFFFFF',
          borderRadius: '16px',
          padding: '60px',
          textAlign: 'center',
          border: '1px solid #E6E8EF'
        }}>
          <ArrowDownLeft size={48} color="#4B9DFF" style={{ marginBottom: '16px' }} />
          <h3 style={{ color: '#0B0B0B', fontSize: '20px', marginBottom: '8px' }}>
            No Transfers Yet
          </h3>
          <p style={{ color: '#666', fontSize: '14px' }}>
            Your transfer history will appear here after you make a purchase
          </p>
        </div>
      )}
    </div>
  );
};

export default BuyerTransfersPage;
